import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '@/services/api';

/**
 * Customer wallet — balance, history, and gift-card redemption for a branch. Balance is spent at the
 * table (staff apply it at checkout). Real card top-ups arrive once the venue's payments are live.
 */
const Wallet: React.FC = () => {
  const { branchId } = useParams<{ branchId: string }>();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [currency, setCurrency] = useState<string | null>(null);
  const [txns, setTxns] = useState<any[]>([]);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!branchId) return;
    try {
      const [b, t] = await Promise.all([
        apiService.getWalletBalance(branchId),
        apiService.getWalletTransactions(branchId),
      ]);
      setBalance(Number((b as any)?.data?.balance) || 0);
      setCurrency((b as any)?.data?.currency || null);
      setTxns(Array.isArray((t as any)?.data) ? (t as any).data : []);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not load your wallet'); }
  };
  useEffect(() => {
    if (!apiService.isAuthenticated()) { navigate('/login'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const money = (v: number) => `${currency ? currency + ' ' : ''}${(Number(v) || 0).toFixed(2)}`;

  const redeem = async () => {
    if (!branchId || !code.trim() || busy) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await apiService.redeemGiftCard(code.trim(), branchId);
      setMsg(`Added ${money(Number((res as any)?.data?.credited) || 0)} to your wallet 🎉`);
      setCode('');
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not redeem that code'); }
    finally { setBusy(false); }
  };

  const label = (t: any) => ({
    topup: 'Top-up', refund: 'Refund', reward: 'Reward', comp: 'Credit from venue',
    gift_redeem: 'Gift card', adjust: 'Adjustment', spend: 'Paid at table',
  } as Record<string, string>)[t.type] || t.type;

  return (
    <div style={s.wrap}>
      <button style={s.back} onClick={() => navigate(-1)}>← Back</button>
      <h1 style={s.h1}>👛 My Wallet</h1>

      <div style={s.card}>
        <div style={s.label}>Balance</div>
        <div style={s.balance}>{money(balance)}</div>
        <div style={s.hint}>Show your table when paying — staff can take it off your bill.</div>
      </div>

      <div style={s.card}>
        <div style={s.label}>Redeem a gift card</div>
        <div style={s.row}>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="GC-XXXXXXXX" style={s.input} autoCapitalize="characters" />
          <button style={s.primary} onClick={redeem} disabled={busy || !code.trim()}>Redeem</button>
        </div>
        {err && <div style={s.err}>{err}</div>}
        {msg && <div style={s.ok}>{msg}</div>}
      </div>

      <h2 style={s.h2}>History</h2>
      {txns.length === 0 ? (
        <div style={s.empty}>No wallet activity yet.</div>
      ) : (
        <div style={s.list}>
          {txns.map((t, i) => (
            <div key={i} style={s.txn}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: '#111827' }}>{label(t)}</div>
                <div style={s.txnMeta}>{new Date(t.created_at).toLocaleString()}{t.note ? ` · ${t.note}` : ''}</div>
              </div>
              <div style={{ fontWeight: 800, color: t.direction === 'credit' ? '#16a34a' : '#b91c1c' }}>
                {t.direction === 'credit' ? '+' : '−'}{money(Number(t.amount) || 0)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  wrap: { maxWidth: 560, margin: '0 auto', padding: 16 },
  back: { background: 'none', border: 'none', color: '#6b7280', fontWeight: 700, cursor: 'pointer', padding: '6px 0', fontSize: 14 },
  h1: { fontSize: 24, fontWeight: 800, margin: '6px 0 14px', color: '#111827' },
  h2: { fontSize: 16, fontWeight: 800, margin: '20px 0 8px', color: '#111827' },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 18, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  label: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#9ca3af' },
  balance: { fontSize: 34, fontWeight: 800, color: '#7e22ce', margin: '4px 0 6px' },
  hint: { color: '#6b7280', fontSize: 12.5 },
  row: { display: 'flex', gap: 8, marginTop: 8 },
  input: { flex: 1, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 15, letterSpacing: '.05em' },
  primary: { background: '#7e22ce', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontWeight: 800, cursor: 'pointer' },
  err: { background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 10, padding: '8px 12px', marginTop: 10, fontSize: 14 },
  ok: { background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 10, padding: '8px 12px', marginTop: 10, fontSize: 14 },
  empty: { color: '#9ca3af', fontSize: 14, padding: '8px 0' },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  txn: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px' },
  txnMeta: { fontSize: 12, color: '#6b7280', marginTop: 2 },
};

export default Wallet;
